// src/components/PaletteSelector.js - Updated to respect theme link opacity settings
import React, { useState, useEffect, useCallback } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { RotateCcw, Sliders, Crown } from 'lucide-react';
import { colorPalettes } from '../palettes';
import styles from './PaletteSelector.module.css';
// Import the ProFeatureContext
import { useProFeature } from '../context/ProFeatureContext';

// Import the palette utilities
import { getProcessedColors, applyPalette } from '../utils/paletteUtils';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Define theme-specific link opacity values (same as in ThemeSelector.js)
const themeLinkOpacities = {
  'classic': 80,
  'gold': 80,
  'minimal': 60,
  'bold': 100,
  // Add other themes as needed
};

const PaletteSelector = ({ userId, onSave, initialPalette, setActiveSection, isProUser, onUpgradeClick, userData }) => {
  // Add this line to access the ProFeatureContext
  const { showProFeatureNotification } = useProFeature();
  
  const [selectedPalette, setSelectedPalette] = useState(
    initialPalette || Object.values(colorPalettes)[0].palettes[0]
  );
  const [isReversed, setIsReversed] = useState(initialPalette?.isReversed || false);
  const [opacity, setOpacity] = useState(initialPalette?.opacity || 100);
  
  // We're removing the link opacity slider, but we'll still keep the state variable
  // to maintain compatibility with the rest of the code
  const [linkOpacity, setLinkOpacity] = useState(initialPalette?.linkOpacity || 100);
  
  const [hasChanges, setHasChanges] = useState(false);

  // Helper function to check if a category is pro-only
  const isProCategory = (categoryKey) => {
    return categoryKey !== 'basic'; // Only 'basic' is free
  };

  // Replace your existing getCurrentColors function with our utility
  const getCurrentColors = useCallback(() => {
    return getProcessedColors(selectedPalette, isReversed, opacity);
  }, [selectedPalette, isReversed, opacity]);

  // Update the effect for applying colors to use our utility
  useEffect(() => {
    if (selectedPalette) {
      const processedPalette = {
        ...selectedPalette,
        colors: getCurrentColors(),
        linkOpacity: linkOpacity // Include link opacity in the palette object
      };
      applyPalette(processedPalette);
      
      // Set the CSS variable for link background opacity separately
      document.documentElement.style.setProperty('--link-background-opacity', linkOpacity / 100);
    }
  }, [getCurrentColors, selectedPalette, linkOpacity]);

  // Modified useEffect to handle theme-specific link opacity
  useEffect(() => {
    if (initialPalette) {
      for (const category of Object.values(colorPalettes)) {
        const matchingPalette = category.palettes.find(
          (p) => p.name === initialPalette.name
        );
        if (matchingPalette) {
          setSelectedPalette(matchingPalette);
          setIsReversed(initialPalette.isReversed || false);
          setOpacity(initialPalette.opacity || 100);
          
          // Check if user has explicitly set a link opacity value
          if (initialPalette.linkOpacitySet) {
            // Use the explicitly set value
            setLinkOpacity(initialPalette.linkOpacity || 100);
          } else if (userData?.profile?.theme) {
            // Use the theme's default value if available
            const themeOpacity = themeLinkOpacities[userData.profile.theme] || 100;
            setLinkOpacity(themeOpacity);
          } else {
            // Fallback to saved value or default
            setLinkOpacity(initialPalette.linkOpacity || 100);
          }
          break;
        }
      }
    }
  }, [initialPalette, userData?.profile?.theme]);

  const handleSelect = (palette, categoryKey) => {
    if (!isProUser && isProCategory(categoryKey)) {
      // Show notification without auto-redirect
      showProFeatureNotification('Upgrade to Pro to unlock premium color palettes');
      return;
    }
    setSelectedPalette(palette);
    setHasChanges(true);
  };

  const handleSave = useCallback(async () => {
    try {
      const updatedPalette = {
        ...selectedPalette,
        colors: getCurrentColors(),
        isReversed,
        opacity,
        linkOpacity,
        linkOpacitySet: selectedPalette.linkOpacitySet || false // Preserve the flag
      };

      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        'profile.palette': updatedPalette,
      });

      if (onSave) onSave(updatedPalette);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving palette:', error);
    }
  }, [selectedPalette, getCurrentColors, isReversed, opacity, linkOpacity, userId, onSave]);

  useEffect(() => {
    if (hasChanges) {
      const timer = setTimeout(handleSave, 1000);
      return () => clearTimeout(timer);
    }
  }, [handleSave, hasChanges]);

  return (
    <div className={styles.paletteSelector}>
      <div className={styles.controls}>
        <label className={styles.toggleControl}>
          <input
            type="checkbox"
            checked={isReversed}
            onChange={(e) => {
              // Available to all users now
              setIsReversed(e.target.checked);
              setHasChanges(true);
            }}
          />
          <RotateCcw size={16} />
          <span>Reverse Colors</span>
        </label>

        <div className={styles.sliderControl}>
          <Sliders size={16} />
          <input
            type="range"
            min="1"
            max="100"
            value={opacity}
            onChange={(e) => {
              // Available to all users now
              setOpacity(Number(e.target.value));
              setHasChanges(true);
            }}
          />
          <span>Palette Opacity: {opacity}%</span>
        </div>
      </div>

      <div className={styles.selectedPaletteDisplay}>
        <div className={styles.paletteColors}>
          {getCurrentColors().map((color, index) => (
            <div
              key={index}
              className={styles.colorDot}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        <div className={styles.paletteName}>
          {selectedPalette ? selectedPalette.name : 'Select a Palette'}
        </div>
      </div>

      <div className={styles.swiperContainer}>
        <Swiper
          modules={[Navigation, Pagination]}
          navigation
          pagination={{ clickable: true }}
          spaceBetween={50}
          slidesPerView={1}
        >
          {Object.entries(colorPalettes).map(([key, category]) => (
            <SwiperSlide key={key}>
              <div className={styles.slideContent}>
                <h2 className={styles.categoryTitle}>
                  {category.name}
                  {isProCategory(key) && !isProUser && (
                    <span className={styles.proBadge}>
                      <Crown size={14} className={styles.menuCrown} />
                      <span>PRO</span>
                    </span>
                  )}
                </h2>
                <div className={styles.paletteGrid}>
                  {category.palettes.map((palette, index) => (
                    <div
                      key={index}
                      onClick={() => handleSelect(palette, key)}
                      className={`${styles.paletteCard} 
                        ${selectedPalette?.name === palette.name ? styles.selected : ''}
                        ${!isProUser && isProCategory(key) ? styles.proOnly : ''}`}
                    >
                      <div className={styles.paletteColors}>
                        {palette.colors.map((color, colorIndex) => (
                          <div
                            key={colorIndex}
                            className={styles.colorDot}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <div className={styles.paletteCardHeader}>
                        <span className={styles.paletteName}>{palette.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default PaletteSelector;